import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { statsApi, type StatKind } from '../../api/stats.js';
import { LineChart } from '../../components/LineChart.js';
import { Spinner, ErrorBox, EmptyBox, Field } from '../../components/ui.js';
import { money, num, datetime } from '../../lib/format.js';

/**
 * 랜덤박스 하나를 골라 일별로 본다.
 *
 * 원본: randombox/order/randombox_order_randombox.php
 * 원본이 GROUP BY ... WITH ROLLUP 으로 합계 행을 함께 받아온다. 그 행은 날짜가
 * 비어 있어서 표 맨 아래로 따로 뺐다.
 */

const TITLES: Record<StatKind, string> = {
  lottery: 'F랜덤박스별 상세',
  randombox: 'N랜덤박스별 상세',
};

/** URL 에 id 가 없을 때 기본으로 골라 둘 값. lottery 쪽은 따로 없다. */
const DEFAULT_ID: Partial<Record<StatKind, string>> = {
  randombox: '4001',
};

export function BoxReportPage({ kind }: { kind: StatKind }) {
  const [params, setParams] = useSearchParams();
  const selected = params.get('id') ?? DEFAULT_ID[kind] ?? '';

  // lottery 쪽은 오나홀(fanza)까지 함께 나온다. 서버가 합쳐서 준다.
  const boxes = useQuery({
    queryKey: ['stats', kind, 'boxes'],
    queryFn: () => statsApi.boxes(kind),
    staleTime: 5 * 60 * 1000,
  });

  const options = boxes.data ?? [];

  const report = useQuery({
    queryKey: ['stats', kind, 'box', selected],
    queryFn: () => statsApi.box(kind, selected),
    enabled: selected !== '',
  });

  const chart = useMemo(() => {
    const rows = report.data?.rows ?? [];
    const labels = rows.map((r) => r.day!).filter(Boolean);
    return {
      labels,
      series: [
        { id: 'tickets', label: '판매매수', points: rows.map((r) => ({ x: r.day!, y: r.tickets })) },
        { id: 'uu', label: '구매자수', points: rows.map((r) => ({ x: r.day!, y: r.uuCount })) },
      ],
    };
  }, [report.data]);

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">{TITLES[kind]}</h1>
          <p className="page-desc">
            랜덤박스 하나의 일별 판매·구매자수·배송비.
            {report.data?.name && ` — ${report.data.name}`}
          </p>
        </div>
        <div className="page-head__actions">
          {report.isFetching && <span className="page-head__busy">갱신 중…</span>}
        </div>
      </header>

      <div className="filters">
        <Field label="랜덤박스">
          <select
            className="input"
            value={selected}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              // 기본값이 있는 kind(randombox) 는 id 를 아예 지우면 selected 계산이
              // 그 기본값으로 다시 튀어 "— 고르세요 —" 를 고를 수 없게 된다.
              // 빈 값을 명시적으로 남겨서 "일부러 비웠다" 와 "아직 안 골랐다" 를 구분한다.
              if (e.target.value) next.set('id', e.target.value);
              else if (DEFAULT_ID[kind]) next.set('id', '');
              else next.delete('id');
              setParams(next, { replace: true });
            }}
          >
            <option value="">— 고르세요 —</option>
            {options.map((o) => (
              <option key={`${o.kind}:${o.id}`} value={o.id}>
                {o.kind === 'fanza' ? '[오나홀] ' : ''}
                {o.name} ({o.id})
              </option>
            ))}
          </select>
        </Field>
      </div>

      {selected === '' ? (
        <EmptyBox>랜덤박스를 고르면 보여줍니다.</EmptyBox>
      ) : report.isPending ? (
        <Spinner />
      ) : report.isError ? (
        <ErrorBox error={report.error} onRetry={() => void report.refetch()} />
      ) : report.data.rows.length === 0 ? (
        <EmptyBox>주문이 없습니다.</EmptyBox>
      ) : (
        <>
          {report.data.total && (
            <div className="summary">
              <div className="summary__item">
                <span className="summary__label">판매매수</span>
                <strong className="summary__value">{num(report.data.total.tickets)}</strong>
              </div>
              <div className="summary__item">
                <span className="summary__label">구매자수</span>
                <strong className="summary__value">{num(report.data.total.uuCount)}</strong>
              </div>
              <div className="summary__item">
                <span className="summary__label">매출</span>
                <strong className="summary__value">{money(report.data.total.sales)}</strong>
              </div>
              <div className="summary__item">
                <span className="summary__label">배송비</span>
                <strong className="summary__value">{money(report.data.total.delivery)}</strong>
              </div>
            </div>
          )}

          <div className="panel">
            <h2 className="panel__title">일별 추이</h2>
            <LineChart labels={chart.labels} series={chart.series} />
          </div>

          <div className="panel">
            <h2 className="panel__title">일별 내역</h2>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>일자</th>
                    <th style={{ textAlign: 'right' }}>판매매수</th>
                    <th style={{ textAlign: 'right' }}>50매미만</th>
                    <th style={{ textAlign: 'right' }}>50매</th>
                    <th style={{ textAlign: 'right' }}>구매자수</th>
                    <th style={{ textAlign: 'right' }}>매출</th>
                    <th style={{ textAlign: 'right' }}>배송비</th>
                    <th>첫 주문</th>
                    <th>마지막 주문</th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.rows.map((r) => (
                    <tr key={r.day}>
                      <td>{r.day}</td>
                      <td style={{ textAlign: 'right' }}>{num(r.tickets)}</td>
                      <td style={{ textAlign: 'right' }}>{num(r.ticketsUnder50)}</td>
                      <td style={{ textAlign: 'right' }}>{num(r.tickets50)}</td>
                      <td style={{ textAlign: 'right' }}>{num(r.uuCount)}</td>
                      <td style={{ textAlign: 'right' }}>{money(r.sales)}</td>
                      <td style={{ textAlign: 'right' }}>{money(r.delivery)}</td>
                      <td className="td-muted">{datetime(r.firstOrder)}</td>
                      <td className="td-muted">{datetime(r.lastOrder)}</td>
                    </tr>
                  ))}
                </tbody>
                {report.data.total && (
                  <tfoot>
                    <tr>
                      <td>합계</td>
                      <td style={{ textAlign: 'right' }}>{num(report.data.total.tickets)}</td>
                      <td style={{ textAlign: 'right' }}>{num(report.data.total.ticketsUnder50)}</td>
                      <td style={{ textAlign: 'right' }}>{num(report.data.total.tickets50)}</td>
                      <td style={{ textAlign: 'right' }}>{num(report.data.total.uuCount)}</td>
                      <td style={{ textAlign: 'right' }}>{money(report.data.total.sales)}</td>
                      <td style={{ textAlign: 'right' }}>{money(report.data.total.delivery)}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {/* 합계의 구매자수는 날짜별 합이 아니라 기간 전체의 중복 없는 사람 수다. */}
            <p className="panel__note">
              합계의 구매자수는 날짜별 숫자를 더한 값이 아니라, 기간 전체에서 중복을 뺀 사람
              수입니다 (여러 날 산 사람은 한 번만 셉니다).
            </p>
          </div>
        </>
      )}
    </section>
  );
}
